import { useLatestRef } from 'js/sign-components/common/use-latest-ref';
import React from 'react';
import { context } from './error-boundary';

export function useCatchError() {
  const boundaryPresent = React.useContext(context);
  const [error, setError] = React.useState<Error | unknown>();
  // I need this to be stable across renders for when it's used in useEffect.
  const impl = React.useRef(
    async <T extends unknown>(p: Promise<T>): Promise<T> => {
      try {
        return await p;
      } catch (error) {
        setError(error);
        throw error;
      }
    },
  );

  if (!boundaryPresent) {
    throw new Error('useCatchContext must be used inside the ErrorBoundary');
  }

  if (error) {
    throw error;
  }


  return impl.current;
}

/**
 * This wraps a callback function so that if it throws, it triggers the
 * ErrorBoundary. It's here for unrecoverable/unexpected errors.
 */
export function useCatchCallback<
  Args extends unknown[],
  Value extends unknown
>(callback: (...args: Args) => Promise<Value>): typeof callback {
  const catchError = useCatchError();
  const ref = useLatestRef(callback);

  return React.useCallback(async (...args: Args) => {
    return catchError(
      ref.current(...args),
    );
  }, [catchError, ref]);
}
